import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bluetooth, Usb, Cable, Printer, Inbox, ScanBarcode, Unplug, Info, Store, Loader2, Smartphone, ImagePlus, X, BadgeCheck } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { fileToLogo } from "../lib/image";
import { LoyaltyNotifTab } from "../components/settings/LoyaltyNotifTab";
import { CloudSyncTab } from "../components/settings/CloudSyncTab";
import { WindowsPrinterPicker } from "../components/settings/WindowsPrinterPicker";
import { IS_DESKTOP } from "../lib/desktop";
import { api, errMsg } from "../lib/api";
import { hardware, support } from "../lib/hardware";
import { useAuth } from "../context/AuthContext";
import { useHardware } from "../context/HardwareContext";
import { useBarcodeScanner } from "../hooks/useBarcodeScanner";
import { PageWrap } from "../components/common";
import { ReceiptPaper } from "../components/Receipt";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BAUDS = ["9600", "19200", "38400", "57600", "115200"];
const sup = support();

const Status = ({ dev }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${dev ? "text-success" : "text-muted-foreground bg-muted"}`}
    style={dev ? { background: "hsl(var(--success) / .12)" } : undefined} data-testid="device-status">
    <span className={`h-2 w-2 rounded-full ${dev ? "bg-success" : "bg-muted-foreground/50"}`} />
    {dev ? `Terkoneksi · ${dev.name}` : "Terputus"}
  </span>
);

const ConnectButtons = ({ role, types, baud }) => {
  const [busy, setBusy] = useState("");
  const go = async (type) => {
    setBusy(type);
    try {
      const d = await hardware.connect(role, type, baud);
      toast.success(`Terhubung: ${d.name}`);
    } catch (e) {
      if (e?.name !== "NotFoundError") toast.error("Gagal terhubung: " + e.message);
    } finally {
      setBusy("");
    }
  };
  const meta = { bluetooth: [Bluetooth, "Bluetooth BLE"], spp: [Bluetooth, "Bluetooth Klasik (SPP)"], usb: [Usb, "USB"], serial: [Cable, "Serial / COM"], rawbt: [Smartphone, "RawBT (Android)"] };
  return (
    <div className="flex flex-wrap gap-2">
      {types.map((t) => {
        const [Icon, label] = meta[t];
        return (
          <Button key={t} variant="outline" disabled={!sup[t] || !!busy} onClick={() => go(t)} data-testid={`connect-${role}-${t}`} title={!sup[t] ? "Tidak didukung browser ini" : ""}>
            {busy === t ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icon className="h-4 w-4 mr-2" />}{label}
          </Button>
        );
      })}
    </div>
  );
};

const Section = ({ icon: Icon, title, desc, children, right }) => (
  <div className="rounded-2xl border bg-card p-5 space-y-4">
    <div className="flex items-start justify-between gap-3">
      <div className="flex gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Icon className="h-5 w-5" /></div>
        <div><h2 className="font-display font-bold">{title}</h2><p className="text-sm text-muted-foreground">{desc}</p></div>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 text-sm py-1">
    <span>{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </label>
);

const VSC_DEVICES = [
  ["VSC TM-58D", "Printer thermal 58mm (384 dot)", IS_DESKTOP ? "Aplikasi desktop Windows: instal driver printer (USB) atau pair Bluetooth (PIN 1234) di Windows, lalu pilih printernya di kotak \"Printer Windows\" dan klik Gunakan Printer Ini." : "Bluetooth: pasangkan dulu di Pengaturan Bluetooth HP/laptop (PIN 1234), lalu klik \"Bluetooth Klasik (SPP)\". Kabel USB: pilih \"USB\"; di Windows bila gagal pakai \"Serial / COM\" atau dialog print."],
  ["VSC CD-408", "Laci uang RJ11 12V/24V", "Colok kabel RJ11 (mirip kabel LAN/telepon) dari laci ke port DK / Cash Drawer di belakang printer — bukan ke komputer. Hubungkan printer dulu (di atas), pilih \"Melalui printer (RJ11)\", pin \"Pin 2 & 5\", lalu klik Tes Buka Laci."],
  ["VSC BT-395S", "Scanner laser Bluetooth / USB", "Mode HID (keyboard): pairing Bluetooth biasa atau colok dongle/kabel USB, langsung bisa scan tanpa pengaturan."],
];

const DevicePresets = ({ updateCfg }) => {
  const { store, setStore, can } = useAuth();
  const apply = async () => {
    updateCfg({ drawerVia: "printer", drawerPin: 2, autoDrawer: true, autoPrint: true });
    if (can("settings") && store?.receipt_width !== "58mm") {
      const { data } = await api.put("/settings", { ...Object.fromEntries(["store_name", "tagline", "address", "phone", "email", "footer", "logo", "print_logo"].map((k) => [k, store[k] ?? ""])), print_logo: !!store.print_logo, tax_percent: Number(store.tax_percent) || 0, receipt_width: "58mm" });
      setStore(data);
    }
    toast.success("Preset VSC diterapkan: kertas 58mm, laci via printer (pin 2 & 5)");
  };
  return (
    <Section icon={BadgeCheck} title="Perangkat Teruji" desc="Panduan cepat untuk perangkat VSC yang Anda gunakan." right={<Button size="sm" onClick={apply} data-testid="apply-vsc-preset-btn">Terapkan Preset VSC</Button>}>
      <div className="grid gap-2">
        {VSC_DEVICES.map(([n, t, d]) => (
          <div key={n} className="rounded-xl bg-muted/50 p-3 text-sm"><span className="font-semibold">{n}</span> <span className="text-muted-foreground">· {t}</span><p className="text-xs text-muted-foreground mt-1">{d}</p></div>
        ))}
      </div>
    </Section>
  );
};

const HardwareTab = () => {
  const { state, cfg, updateCfg, testPrint, openDrawer, browserPrint } = useHardware();
  const { store } = useAuth();
  const [baud, setBaud] = useState("9600");
  const [lastScan, setLastScan] = useState("");
  useBarcodeScanner(setLastScan);
  useEffect(() => hardware.onScan(setLastScan), []);

  const sample = { invoice: "INV-CONTOH-0001", created_at: new Date().toISOString(), cashier_name: "Kasir", items: [{ name: "Produk Contoh", qty: 2, price: 15000, discount: 0 }],
    subtotal: 30000, discount: 0, tax: 0, total: 30000, paid: 50000, change: 20000, payment_method: "tunai", status: "selesai" };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-6">
        {!sup.bluetooth && !sup.usb && !sup.serial && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm flex gap-3" data-testid="hw-unsupported-warning">
            <Info className="h-5 w-5 text-warning shrink-0" />
            Browser ini tidak mendukung koneksi langsung (Bluetooth/USB/Serial). Gunakan Google Chrome atau Microsoft Edge di laptop / Android. Struk tetap dapat dicetak via dialog print.
          </div>
        )}
        <DevicePresets updateCfg={updateCfg} />
        <Section icon={Printer} title="Printer Thermal (ESC/POS)" desc="Hubungkan printer struk 58mm / 80mm via Bluetooth, kabel USB, atau Serial/COM." right={<Status dev={state.printer} />}>
          {IS_DESKTOP && <WindowsPrinterPicker />}
          {IS_DESKTOP && <p className="text-xs text-muted-foreground">Koneksi alternatif:</p>}
          <div className="flex flex-wrap items-end gap-3">
            <ConnectButtons role="printer" types={["spp", "bluetooth", "usb", "serial", "rawbt"]} baud={baud} />
            <div className="space-y-1">
              <Label className="text-xs">Baud rate (Serial)</Label>
              <Select value={baud} onValueChange={setBaud}>
                <SelectTrigger className="w-28 h-9" data-testid="baud-select"><SelectValue /></SelectTrigger>
                <SelectContent>{BAUDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={testPrint} disabled={!state.printer} data-testid="test-print-btn"><Printer className="h-4 w-4 mr-2" />Tes Print</Button>
            <Button variant="outline" onClick={() => browserPrint(sample)} data-testid="test-browser-print-btn">Tes Print via Browser</Button>
            {state.printer && <Button variant="ghost" className="text-destructive" onClick={() => hardware.disconnect("printer")} data-testid="disconnect-printer-btn"><Unplug className="h-4 w-4 mr-2" />Putuskan</Button>}
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 pt-2 border-t">
            <Toggle label="Cetak struk otomatis setelah bayar" checked={cfg.autoPrint} onChange={(v) => updateCfg({ autoPrint: v })} testid="cfg-auto-print" />
            <Toggle label="Pakai dialog print browser jika printer tidak terhubung" checked={cfg.browserFallback} onChange={(v) => updateCfg({ browserFallback: v })} testid="cfg-browser-fallback" />
            <div className="flex items-center justify-between text-sm py-1">
              <span>Jumlah salinan struk</span>
              <Input className="w-16 h-8 font-mono text-center" inputMode="numeric" value={cfg.copies} onChange={(e) => updateCfg({ copies: Math.max(1, Math.min(5, Number(e.target.value.replace(/\D/g, "")) || 1)) })} data-testid="cfg-copies" />
            </div>
          </div>
        </Section>

        <Section icon={Inbox} title="Laci Uang Otomatis" desc="Laci standar (RJ11) terhubung ke port DK printer dan dibuka lewat sinyal ESC/POS. Laci USB-serial dapat dihubungkan langsung."
          right={<Status dev={cfg.drawerVia === "serial" ? state.drawer : state.printer} />}>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Koneksi laci</Label>
              <Select value={cfg.drawerVia} onValueChange={(v) => updateCfg({ drawerVia: v })}>
                <SelectTrigger className="w-48" data-testid="drawer-via-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="printer">Melalui printer (RJ11)</SelectItem><SelectItem value="serial">Serial / USB langsung</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1 w-full sm:w-auto">
              <Label className="text-xs">Pin sinyal</Label>
              <Select value={String(cfg.drawerPin)} onValueChange={(v) => updateCfg({ drawerPin: Number(v) })}>
                <SelectTrigger className="w-full sm:w-56" data-testid="drawer-pin-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">Pin 2 (umum)</SelectItem><SelectItem value="1">Pin 5</SelectItem><SelectItem value="2">Pin 2 & 5 (paling kompatibel)</SelectItem></SelectContent>
              </Select>
            </div>
            {cfg.drawerVia === "serial" && <ConnectButtons role="drawer" types={["serial", "spp", "usb"]} baud={baud} />}
          </div>
          {cfg.drawerVia === "printer" && !state.printer && (
            <div className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-xs flex gap-2" data-testid="drawer-needs-printer-warning">
              <Info className="h-4 w-4 text-warning shrink-0" />Laci RJ11 dibuka oleh printer. Hubungkan printer struk terlebih dahulu di bagian Printer Thermal di atas (cetak via dialog browser tidak bisa membuka laci).
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={openDrawer} data-testid="test-drawer-btn"><Inbox className="h-4 w-4 mr-2" />Tes Buka Laci</Button>
            {state.drawer && <Button variant="ghost" className="text-destructive" onClick={() => hardware.disconnect("drawer")} data-testid="disconnect-drawer-btn"><Unplug className="h-4 w-4 mr-2" />Putuskan</Button>}
          </div>
          <div className="pt-2 border-t">
            <Toggle label="Buka laci otomatis saat pembayaran tunai" checked={cfg.autoDrawer} onChange={(v) => updateCfg({ autoDrawer: v })} testid="cfg-auto-drawer" />
          </div>
        </Section>

        <Section icon={ScanBarcode} title="Scanner Barcode" desc="Scanner USB & Bluetooth (mode keyboard/HID) langsung berfungsi tanpa pengaturan. Scanner mode Serial/COM dapat dihubungkan di sini. Di HP, gunakan tombol Kamera di layar Kasir."
          right={<Status dev={state.scanner} />}>
          <div className="flex flex-wrap gap-2">
            <ConnectButtons role="scanner" types={["serial", "spp"]} baud={baud} />
            {state.scanner && <Button variant="ghost" className="text-destructive" onClick={() => hardware.disconnect("scanner")} data-testid="disconnect-scanner-btn"><Unplug className="h-4 w-4 mr-2" />Putuskan</Button>}
          </div>
          <div className="rounded-xl bg-muted/60 p-4">
            <div className="text-xs text-muted-foreground mb-1">Tes scanner: scan barcode apa saja sekarang</div>
            <div className="font-mono font-bold text-lg" data-testid="scanner-test-result">{lastScan || "— menunggu scan —"}</div>
          </div>
          <Toggle label="Bunyi beep saat scan berhasil" checked={cfg.scanBeep} onChange={(v) => updateCfg({ scanBeep: v })} testid="cfg-scan-beep" />
        </Section>

        <div className="rounded-xl border p-4 text-sm text-muted-foreground space-y-1.5">
          <div className="font-semibold text-foreground flex items-center gap-2"><Info className="h-4 w-4" />Panduan koneksi</div>
          <p>• Bluetooth Klasik (SPP): untuk printer seperti VSC TM-58D. Pasangkan (pair) printer di pengaturan Bluetooth perangkat lebih dulu, lalu pilih di daftar. Didukung Chrome Android & Chrome/Edge laptop terbaru.</p>
          <p>• Bluetooth BLE: untuk printer Bluetooth Low Energy.</p>
          <p>• RawBT (Android): alternatif bila SPP tidak muncul — instal aplikasi RawBT dari Play Store, pilih printer di RawBT, lalu klik tombol RawBT di sini.</p>
          <p>• USB (kabel): klik "USB" lalu pilih printer. Jika di Windows tidak muncul / gagal, gunakan "Serial / COM" (driver virtual COM) atau aktifkan dialog print browser.</p>
          <p>• Perangkat yang pernah terhubung via USB/Serial akan terhubung kembali otomatis saat aplikasi dibuka.</p>
          <p>• iPhone/iPad tidak mendukung koneksi langsung; struk dicetak via dialog print (AirPrint).</p>
        </div>
      </div>
      <div className="space-y-3">
        <h3 className="font-display font-bold">Pratinjau Struk ({store?.receipt_width})</h3>
        <div className="rounded-2xl bg-muted/60 py-6 overflow-x-auto">{store && <ReceiptPaper tx={sample} store={store} width={store.receipt_width} />}</div>
      </div>
    </div>
  );
};

const LogoField = ({ value, onChange }) => {
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus gambar");
    onChange(await fileToLogo(file));
  };
  return (
    <div className="sm:col-span-2 flex items-center gap-4">
      <div className="h-20 w-20 rounded-xl border bg-white grid place-items-center overflow-hidden" data-testid="store-logo-preview">
        {value ? <img src={value} alt="Logo toko" className="max-h-full max-w-full object-contain" /> : <ImagePlus className="h-6 w-6 text-slate-400" />}
      </div>
      <div className="space-y-2">
        <Label>Logo Toko</Label>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" asChild>
            <label className="cursor-pointer" data-testid="store-logo-upload-btn">Unggah Logo<input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="store-logo-input" /></label>
          </Button>
          {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid="store-logo-remove-btn"><X className="h-4 w-4 mr-1" />Hapus</Button>}
        </div>
        <p className="text-xs text-muted-foreground">PNG/JPG, otomatis diperkecil. Logo hitam-putih kontras paling bagus untuk struk.</p>
      </div>
    </div>
  );
};

const StoreTab = () => {
  const { store, setStore } = useAuth();
  const [f, setF] = useState(store);
  const [saving, setSaving] = useState(false);
  useEffect(() => setF(store), [store]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = Object.fromEntries(["store_name", "tagline", "address", "phone", "email", "footer", "logo", "print_logo", "receipt_width"].map((k) => [k, f[k] ?? (k === "print_logo" ? false : "")]));
      const { data } = await api.put("/settings", { ...body, tax_percent: Number(f.tax_percent) || 0 });
      setStore(data);
      toast.success("Pengaturan toko disimpan");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="rounded-2xl border bg-card p-5 grid sm:grid-cols-2 gap-4 max-w-3xl">
      <LogoField value={f.logo} onChange={(logo) => setF({ ...f, logo })} />
      <div className="space-y-1.5 sm:col-span-2"><Label>Nama Toko</Label><Input required value={f.store_name} onChange={set("store_name")} data-testid="store-name-input" /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Tagline</Label><Input value={f.tagline || ""} onChange={set("tagline")} placeholder="cth: Belanja hemat, lengkap & dekat" data-testid="store-tagline-input" /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Alamat</Label><Textarea rows={2} value={f.address} onChange={set("address")} data-testid="store-address-input" /></div>
      <div className="space-y-1.5"><Label>Telepon</Label><Input value={f.phone} onChange={set("phone")} data-testid="store-phone-input" /></div>
      <div className="space-y-1.5"><Label>Email Toko</Label><Input type="email" value={f.email || ""} onChange={set("email")} data-testid="store-email-input" /></div>
      <div className="space-y-1.5"><Label>Pajak / PPN (%)</Label><Input inputMode="decimal" className="font-mono" value={f.tax_percent} onChange={set("tax_percent")} data-testid="store-tax-input" /></div>
      <div className="space-y-1.5">
        <Label>Lebar Kertas Struk</Label>
        <Select value={f.receipt_width} onValueChange={(v) => setF({ ...f, receipt_width: v })}>
          <SelectTrigger data-testid="store-receipt-width-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="58mm">58 mm (32 karakter)</SelectItem><SelectItem value="80mm">80 mm (48 karakter)</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Catatan Kaki Struk</Label><Input value={f.footer} onChange={set("footer")} data-testid="store-footer-input" /></div>
      <label className="sm:col-span-2 flex items-center justify-between text-sm rounded-lg border px-3 py-2">
        <span>Cetak logo toko di struk (printer thermal)</span>
        <Switch checked={!!f.print_logo} onCheckedChange={(v) => setF({ ...f, print_logo: v })} disabled={!f.logo} data-testid="store-print-logo-switch" />
      </label>
      <div className="sm:col-span-2"><Button type="submit" disabled={saving} data-testid="store-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Simpan Pengaturan</Button></div>
    </form>
  );
};

export default function Settings() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const isAdmin = can("settings");
  const { isOwner } = useAuth();
  return (
    <PageWrap>
      <Tabs defaultValue={isAdmin && ["store", "member", "cloud"].includes(params.get("tab")) && (params.get("tab") !== "cloud" || isOwner) ? params.get("tab") : "hardware"}>
        <TabsList>
          <TabsTrigger value="hardware" data-testid="tab-hardware"><Printer className="h-4 w-4 mr-2" />Perangkat</TabsTrigger>
          {isAdmin && <TabsTrigger value="store" data-testid="tab-store"><Store className="h-4 w-4 mr-2" />Toko & Struk</TabsTrigger>}
          {isAdmin && <TabsTrigger value="member" data-testid="tab-member">Member & Notifikasi</TabsTrigger>}
          {isOwner && <TabsTrigger value="cloud" data-testid="tab-cloud">Cloud & Desktop</TabsTrigger>}
        </TabsList>
        <TabsContent value="hardware" className="mt-6"><HardwareTab /></TabsContent>
        {isAdmin && <TabsContent value="store" className="mt-6"><StoreTab /></TabsContent>}
        {isAdmin && <TabsContent value="member" className="mt-6"><LoyaltyNotifTab /></TabsContent>}
        {isOwner && <TabsContent value="cloud" className="mt-6"><CloudSyncTab /></TabsContent>}
      </Tabs>
    </PageWrap>
  );
}
